import { AuthContextInterface, withAuthContext } from '@amzn/awscat-react-components';
import { useContext, useEffect } from 'react';

import { constants } from './common/Localization';
import { coreResources } from './common/resourceConstants';
import { isAWSUser } from './common/util';
import whatsNew from './components/whats-new/releases.json';
import { HelpPanelContext } from './contexts/HelpPanelContext';
import { NavigationContext } from './contexts/NavigationContext';
import LandingPage from './LandingPage';

const Landing = (props: AuthContextInterface) => {
    const isAWS = isAWSUser(props.auth);
    const { setNavigationClosed } = useContext(NavigationContext);
    const { panelHidden } = useContext(HelpPanelContext);

    useEffect(() => {
        setNavigationClosed();
        panelHidden();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const features = [
        {
            title: 'Manage portfolio data',
            description: <p>Import and validate portfolio data with a flexible import workflow and customizable data validation rules.</p>,
        },
        {
            title: 'Collect portfolio data',
            description: <p>Request and collect application, server and database data from the subject matter experts via customizable questionnaires.</p>,
        },
        {
            title: 'Estimate AWS usage cost',
            description: (<ul>
                <li>Compute: EC2, VMware Cloud on AWS</li>
                <li>Storage: EBS, EFS, S3, Glacier</li>
                <li>Database: RDS</li>
                <li>Network cost estimation</li>
                <li>IT labor and support cost estimation</li>
            </ul>),
        },
        {
            title: 'Prioritize applications',
            description: <p>Rank applications based on the business and technical drivers of the migration.</p>
        },
        {
            title: 'Compare AWS and on-premises costs',
            description: <p>See detailed cost and cash flow comparison between AWS and on-premises analysis in both numeric and graphical formats</p>
        },
        {
            title: 'Create application groups',
            description: <p>Create application groups by analyzing dependencies to understand which applications must migrate together.</p>,
        },
        {
            title: 'Estimate migration project cost',
            description: <p>Estimate the cost of an on-premises to AWS migration project.</p>,
        },
        {
            title: 'Build a migration wave plan',
            description: (<p>Generate a wave plan for application groups. MPA helps to consider an application’s priority,
                data collection status and dependencies so that migration can start sooner with fewer potential roadblocks.</p>),
        },
        {
            title: 'Recommend migration strategies',
            description: <p>Recommend the migration strategy (Rehost, Replatform, Rearchitect) for the servers and applications in the portfolio.</p>,
        },
        {
            title: 'Visualize portfolio',
            description: <p>Visualize the portfolio data through customizable charts.</p>
        },
        {
            title: 'Share analysis',
            description: <p>Share portfolios and collaborate with other MPA users.</p> },
        {title: 'Download assessment', description: <p>Download reports in a printable format and to share the analysis and use in presentations.</p> },
    ];

    const useCases = [
        {title: 'Analyze portfolio discovery result or a CMDB extract', description: '', learnMoreLinkLocation: ''},
        {title: 'Consolidate and manage portfolio data', description: '', learnMoreLinkLocation: ''},
        {title: 'Share a portfolio for collaboration', description: '', learnMoreLinkLocation: ''},
    ];

    // TODO: update this to prod url for A2T when that launches
    const relatedServices = [
        {
            title: 'AWS Assessment Tool',
            serviceLinkLocation: 'https://mra-proserve.amazonaws.com/',
            description: 'Measure, assess, and accelerate the journey to the cloud'
        },
        {
            title: 'AWS Prescriptive Guidance Library',
            serviceLinkLocation: 'https://apg-library.amazonaws.com/',
            description: 'Library of AWS migration patterns'
        },
    ];

    const moreResources = coreResources.map(resource => ({
        linkLocation: resource.href,
        linkText: resource.text,
    }));

    if (isAWS) {
        moreResources.push(
            {linkLocation: 'https://tiny.amazon.com/11f63brc3', linkText: 'Submit an issue'},
            {linkLocation: 'https://tiny.amazon.com/1bypqhlrz', linkText: 'Request a feature'},
            {linkLocation: 'https://tiny.amazon.com/t33jux2n', linkText: 'Subscribe to updates'},
            {linkLocation: 'https://slack.com/app_redirect?channel=portfolio-assessment', linkText: 'Join us on Slack at #portfolio-assessment'},
        );
    } else {
        moreResources.push({linkLocation: 'mailto:proserve-mpa-tool@amazon.com', linkText: 'Contact us'});
    }

    return (
        <LandingPage
            title={constants.AWS_PORTFOLIO_ASSESSMENT_TITLE}
            serviceName={constants.AWS_PORTFOLIO_ASSESSMENT_TITLE}
            majorDescription={constants.AWS_PORTFOLIO_ASSESSMENT_DESC}
            features={features}
            whatsNew={whatsNew.slice(0, 3)}
            moreResources={moreResources}
            useCases={useCases}
            relatedServices= {relatedServices}
        />
    );
};

export default withAuthContext(Landing);
